import {
  AppInventoryOption,
  type AppInventoryOptionType,
  type AppInventoryState,
  AppInventoryVersion,
  type AppInventoryVersionType,
} from '@openx/types/targeting/appInventory';
import {
  type AppInventoryType,
  type InventoryAndContentState,
  InventoryContentOption,
  type ParentAccountFeatures,
} from '@openx/types/targeting/inventoryAndContent';
import type { ComparisonType } from '@openx/types/targeting/targetingValuesTypes';
import type { AnyAction } from '@reduxjs/toolkit';
import type { Dispatch, SetStateAction } from 'react';
import { useTargetingContext } from '../../../../utils/context/TargetingContext';
import type { SetComparisonTypeDispatch } from '../../../InventoryAndContent/InventoryAndContentTopBox/constants';
import { InventoryTopBoxRenderer } from '../../../InventoryAndContent/InventoryAndContentTopBox/partials/InventoryTopBoxRenderer';
import type { DrawerComponentsMap } from '../../../InventoryAndContent/InventoryAndContentTopBox/utils/filterTypes';
import type { InvalidItemsError } from '../../BulkAdd/types';
import { OaAppBundleIdTopBoxRenderer } from './OaAppBundleIdTopBoxRenderer';

export type TopBoxRendererProps = {
  comparisonType: ComparisonType;
  targetingParams: InventoryAndContentState | AppInventoryState;
  parentAccountFeatures: ParentAccountFeatures;
  accountUid: string;
  showBulkAdd: boolean;
  isLimitReached: boolean;
  contentOption: InventoryContentOption | AppInventoryOptionType;
  setContentOption: Dispatch<SetStateAction<InventoryContentOption>> | Dispatch<SetStateAction<AppInventoryOptionType>>;
  onBulkOpen: () => void;
  setComparisonType: SetComparisonTypeDispatch;
  dispatchInventory: (action: AnyAction) => void;
  setAppBundleIdDuplicateErrors: Dispatch<SetStateAction<InvalidItemsError | null>>;
  inventoryVersion?: AppInventoryVersionType;
  drawerContentBase: Partial<DrawerComponentsMap>;
  // App Inventory specific props
  appInventoryType?: AppInventoryType;
};

export const TopBoxRenderer = ({
  comparisonType,
  contentOption,
  parentAccountFeatures,
  showBulkAdd,
  isLimitReached,
  targetingParams,
  setContentOption,
  onBulkOpen,
  dispatchInventory,
  setComparisonType,
  accountUid = '',
  setAppBundleIdDuplicateErrors,
  // App Inventory specific props
  appInventoryType,
  inventoryVersion,
  drawerContentBase,
}: TopBoxRendererProps) => {
  const { isOa } = useTargetingContext();

  const drawerContent =
    inventoryVersion === AppInventoryVersion.APP_INVENTORY_EXCLUDED_V1
      ? Object.fromEntries(
          Object.entries(drawerContentBase).filter(
            ([key]) => key !== AppInventoryOption.APP_GROUP && key !== AppInventoryOption.APP_BUNDLE_ID
          )
        )
      : drawerContentBase;

  if (isOa && contentOption === InventoryContentOption.APP_BUNDLE_ID) {
    return (
      <OaAppBundleIdTopBoxRenderer
        comparisonType={comparisonType}
        inventoryContent={targetingParams[InventoryContentOption.APP_BUNDLE_ID]}
        parentAccountFeatures={parentAccountFeatures}
        accountUid={accountUid}
        showBulkAdd={showBulkAdd}
        isLimitReached={isLimitReached}
        dispatchInventory={dispatchInventory}
        setComparisonType={setComparisonType}
        setContentOption={setContentOption}
        setAppBundleIdDuplicateErrors={setAppBundleIdDuplicateErrors}
        onBulkOpen={onBulkOpen}
        inventoryVersion={inventoryVersion}
      />
    );
  }

  return (
    <InventoryTopBoxRenderer
      contentOption={contentOption}
      parentAccountFeatures={parentAccountFeatures}
      setContentOption={setContentOption}
      showBulkAdd={showBulkAdd}
      isLimitReached={isLimitReached}
      onBulkOpen={onBulkOpen}
      drawerContent={drawerContent}
      appInventoryType={appInventoryType}
      inventoryVersion={inventoryVersion}
    />
  );
};
